import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import Close from "@mui/icons-material/Close";
import pestReportService from "../../api/pestReportService";
import TimeInput24H from "../../components/TimeInput24H";
import { resolveBusinessDateTime } from "../../utils/dateUtils";
import { photosToPdf } from "./photosToPdf";

function padTime(value) {
  const [h, m = "00"] = String(value || "").split(":");
  if (!h || Number.isNaN(Number(h))) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`.slice(0, 5);
}

const LEVELS = [
  { value: 0, label: "0", color: "#15803D" },
  { value: 1, label: "1", color: "#A16207" },
  { value: 2, label: "2", color: "#C2410C" },
  { value: 3, label: "3", color: "#B91C1C" },
];

/** Solo 0–3 enteros; evita borradores o valores raros que fallen en el API. */
function normalizePestLevel(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n > 3) return null;
  return n;
}

const MAX_PHOTOS = 8;
const MAX_PDF_BYTES = 10 * 1024 * 1024;
const DRAFT_KEY = "bremar-agenda-report";
let photoSeq = 0;

export function readReportDraft() {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
  } catch {
    return null;
  }
}

export function patchReportDraft(patch) {
  const prev = readReportDraft() || {};
  localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...prev, ...patch }));
}

export function clearReportDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

function openDraftDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("bremar-report-drafts", 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("drafts")) {
        request.result.createObjectStore("drafts");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadServiceDraft(serviceId) {
  if (!serviceId) return null;
  const db = await openDraftDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction("drafts", "readonly");
      const request = tx.objectStore("drafts").get(serviceId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

export async function saveServiceDraft(serviceId, record) {
  if (!serviceId) return;
  const db = await openDraftDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction("drafts", "readwrite");
      tx.objectStore("drafts").put(record, serviceId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function clearServiceDraft(serviceId) {
  if (!serviceId) return;
  const db = await openDraftDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction("drafts", "readwrite");
      tx.objectStore("drafts").delete(serviceId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
  const pointer = readReportDraft();
  if (pointer?.serviceId === serviceId) clearReportDraft();
}

function isPhoto(file, fromCamera) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (!file?.size) return "empty";
  if (type.startsWith("video/")) return "bad";
  if (type === "image/heic" || type === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif")) {
    return "heic";
  }
  if (type === "application/pdf" || name.endsWith(".pdf")) return "bad";
  if (type.startsWith("image/") || type === "image/jpg" || type === "image/pjpeg") return "ok";
  const imageName = /\.(jpe?g|png|webp|gif)$/i.test(name);
  if (imageName) return "ok";
  if (fromCamera && (!type || type === "application/octet-stream")) return "ok";
  return "bad";
}

function asPdfFile(file) {
  if (file.type === "application/pdf") return file;
  const name = file.name && file.name.toLowerCase().endsWith(".pdf") ? file.name : "reporte.pdf";
  return new File([file], name, { type: "application/pdf" });
}

function isPdfFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return (
    type === "application/pdf"
    || name.endsWith(".pdf")
    || (type === "application/octet-stream" && name.endsWith(".pdf"))
  );
}

export default function ReportPanel({ service, place, alreadyReported, onClose, onSaved }) {
  const serviceId = String(service?.originalServiceId || service?._id || "");
  const [pestLevel, setPestLevel] = useState(null);
  const [pestLevelError, setPestLevelError] = useState(false);
  const [entryTime, setEntryTime] = useState(padTime(service?.hour));
  const [exitTime, setExitTime] = useState("");
  const [photos, setPhotos] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;

  useEffect(() => {
    let cancel = false;
    setReady(false);
    setPestLevel(null);
    setPestLevelError(false);
    loadServiceDraft(serviceId)
      .then((saved) => {
        if (cancel || !saved) return;
        const level = normalizePestLevel(saved.pestLevel);
        if (level !== null) setPestLevel(level);
        if (saved.entryTime) setEntryTime(saved.entryTime);
        if (saved.exitTime) setExitTime(saved.exitTime);
        if (saved.photos?.length) {
          setPhotos(saved.photos.map((photo) => {
            const file = new File([photo.blob], photo.name || "foto.jpg", { type: photo.type || "image/jpeg" });
            return { id: ++photoSeq, file, url: URL.createObjectURL(file) };
          }));
        }
        if (saved.pdf?.blob) {
          setPdfFile(new File([saved.pdf.blob], saved.pdf.name || "reporte.pdf", { type: saved.pdf.type || "application/pdf" }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancel) setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [serviceId]);

  useEffect(() => {
    if (!ready) return undefined;
    const handle = setTimeout(() => {
      saveServiceDraft(serviceId, {
        pestLevel,
        entryTime,
        exitTime,
        photos: photos.map((photo) => ({
          name: photo.file.name,
          type: photo.file.type,
          blob: photo.file,
        })),
        pdf: pdfFile ? { name: pdfFile.name, type: pdfFile.type, blob: pdfFile } : null,
      }).catch(() => {});
    }, 150);
    return () => clearTimeout(handle);
  }, [ready, serviceId, pestLevel, entryTime, exitTime, photos, pdfFile]);

  const selectPestLevel = (value) => {
    const level = normalizePestLevel(value);
    if (level === null) return;
    setPestLevel(level);
    setPestLevelError(false);
    setError("");
  };

  const pestLevelChosen = normalizePestLevel(pestLevel);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const openCamera = async () => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Este teléfono no permite la cámara dentro de la página. Usa Adjuntar.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      setCameraOn(true);
    } catch {
      setError("No se pudo abrir la cámara. Revisa el permiso o usa Adjuntar.");
    }
  };

  useEffect(() => {
    if (!cameraOn || !videoRef.current || !streamRef.current) return undefined;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
    return undefined;
  }, [cameraOn]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) {
      setCameraError("Espera a que se vea la imagen.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError("No se pudo guardar la foto.");
        return;
      }
      const file = new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" });
      addPhotos([file], true);
    }, "image/jpeg", 0.85);
  };

  useEffect(() => () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
  }, []);

  const clearPhotos = () => {
    setPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.url));
      return [];
    });
  };

  const addPhotos = (list, fromCamera) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    setPdfFile(null);
    const accepted = [];
    let message = "";
    for (const file of incoming) {
      const kind = isPhoto(file, fromCamera);
      if (kind === "heic") {
        message = "Esa foto está en formato HEIC. Tómala con el botón de cámara.";
        continue;
      }
      if (kind === "empty") {
        message = "La foto llegó vacía. Tómala de nuevo.";
        continue;
      }
      if (kind !== "ok") {
        message = "Solo se aceptan fotos";
        continue;
      }
      accepted.push({ id: ++photoSeq, file, url: URL.createObjectURL(file) });
    }
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      accepted.forEach((photo) => URL.revokeObjectURL(photo.url));
      setError(`Máximo ${MAX_PHOTOS} fotos`);
      return;
    }
    const kept = accepted.slice(0, room);
    accepted.slice(room).forEach((photo) => URL.revokeObjectURL(photo.url));
    if (accepted.length > room) message = `Máximo ${MAX_PHOTOS} fotos`;
    if (kept.length) setPhotos((current) => [...current, ...kept]);
    setError(message);
  };

  const selectPdf = (file) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      setError("El archivo tiene que ser un PDF");
      return;
    }
    if (!file.size) {
      setError("El PDF está vacío");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setError("El PDF no puede pasar de 10 MB");
      return;
    }
    clearPhotos();
    setPdfFile(asPdfFile(file));
    setError("");
  };

  /** Un solo flujo: fotos (varias) o un PDF, nunca mezclados. */
  const handleAttachFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const pdfs = files.filter(isPdfFile);
    const images = files.filter((f) => !isPdfFile(f));

    if (pdfs.length && images.length) {
      setError("Elige solo fotos o un PDF, no ambos a la vez");
      return;
    }
    if (pdfs.length > 1) {
      setError("Solo puedes adjuntar un PDF");
      return;
    }
    if (pdfs.length === 1) {
      selectPdf(pdfs[0]);
      return;
    }
    addPhotos(images, false);
  };

  const removePhoto = (id) => {
    setPhotos((current) => {
      const photo = current.find((item) => item.id === id);
      if (photo) URL.revokeObjectURL(photo.url);
      return current.filter((item) => item.id !== id);
    });
  };

  const submit = async () => {
    if (saving) return;
    setError("");
    if (alreadyReported) return setError("Este servicio ya tiene reporte");
    if (pestLevelChosen === null) {
      setPestLevelError(true);
      return setError("Elige el nivel de plaga (0, 1, 2 o 3)");
    }
    if (!entryTime || !exitTime) return setError("Indica la hora de entrada y de salida");
    if (!pdfFile && !photos.length) return setError("Toma fotos con la cámara o adjunta fotos o un PDF");

    const entry = resolveBusinessDateTime(entryTime);
    let exit = resolveBusinessDateTime(exitTime);
    if (Number.isNaN(entry.getTime()) || Number.isNaN(exit.getTime())) {
      return setError("Revisa las horas");
    }
    if (exit <= entry) exit.setDate(exit.getDate() + 1);
    const hours = (exit - entry) / (1000 * 60 * 60);
    if (hours <= 0 || hours > 16) return setError("La duración debe ser mayor a 0 y menor a 16 horas");

    const eventId = service.originalServiceId || service._id;
    if (!eventId) return setError("Este servicio no se puede reportar");

    setSaving(true);
    try {
      const reportPdf = pdfFile || await photosToPdf(photos.map((photo) => photo.file));
      await pestReportService.createPestReport({
        eventId,
        serviceId: eventId,
        pestLevel: pestLevelChosen,
        entryTime: entry.toISOString(),
        exitTime: exit.toISOString(),
        reportPdf,
      });
      onSaved();
    } catch (err) {
      setError(err.message || "No se pudo guardar el reporte");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid #EEF0F3",
          pt: "calc(env(safe-area-inset-top) + 10px)",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label="Cerrar"
          sx={{ bgcolor: "#F3F4F6", width: 38, height: 38, "&:hover": { bgcolor: "#E5E7EB" } }}
        >
          <Close />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#111", letterSpacing: "-0.02em" }}>
            Nuevo reporte
          </Typography>
          <Typography noWrap sx={{ fontSize: 13.5, color: "#6B7280" }}>{place}</Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 1, pb: 2 }}>
        {alreadyReported && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Este servicio ya tiene un reporte. Si guardas otro, quedará un segundo registro.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography sx={sectionLabelSx}>Nivel de plaga</Typography>
        <Typography sx={{ fontSize: 12.5, color: pestLevelError ? "#DC2626" : "#6B7280", mb: 1, fontWeight: pestLevelError ? 600 : 400 }}>
          {pestLevelChosen === null
            ? "Obligatorio: toca un número del 0 al 3"
            : `Nivel seleccionado: ${pestLevelChosen}`}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1,
            mb: 2.5,
            p: 0.5,
            borderRadius: "14px",
            border: "1.5px solid",
            borderColor: pestLevelError ? "#DC2626" : "#E5E7EB",
          }}
        >
          {LEVELS.map((level) => {
            const active = pestLevelChosen === level.value;
            return (
              <Box
                key={level.value}
                component="button"
                type="button"
                disabled={saving}
                onClick={() => selectPestLevel(level.value)}
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  cursor: saving ? "default" : "pointer",
                  minHeight: 54,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 19,
                  fontWeight: 800,
                  borderRadius: "13px",
                  border: "1.5px solid",
                  borderColor: active ? level.color : "#E5E7EB",
                  bgcolor: active ? level.color : "#fff",
                  color: active ? "#fff" : level.color,
                  opacity: saving ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {level.label}
              </Box>
            );
          })}
        </Box>

        <Typography sx={sectionLabelSx}>Horario</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
          Escribe la hora en 24 h. Ejemplo: 1430.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2.5 }}>
          <TimeInput24H
            label="Entrada"
            value={entryTime}
            disabled={saving}
            onChange={(e) => setEntryTime(e.target.value)}
            sx={timeFieldSx}
          />
          <TimeInput24H
            label="Salida"
            value={exitTime}
            disabled={saving}
            onChange={(e) => setExitTime(e.target.value)}
            sx={timeFieldSx}
          />
        </Box>

        <Typography sx={sectionLabelSx}>Reporte</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.25 }}>
          Cámara para tomar fotos aquí, o adjunta desde el teléfono (hasta {MAX_PHOTOS} fotos o un PDF). El avance se guarda si recarga la página.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={saving || Boolean(pdfFile) || photos.length >= MAX_PHOTOS}
            onClick={openCamera}
            sx={mediaBtnSx}
          >
            Cámara
          </Button>
          <Button
            component="label"
            variant="outlined"
            disabled={saving || photos.length >= MAX_PHOTOS}
            sx={mediaBtnOutlinedSx}
          >
            Adjuntar
            <input
              hidden
              type="file"
              accept="image/*,application/pdf,.pdf"
              multiple
              disabled={saving}
              onChange={(e) => {
                handleAttachFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </Button>
        </Box>
        {pdfFile && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1.5 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 650, minWidth: 0 }}>{pdfFile.name}</Typography>
            <Button size="small" disabled={saving} onClick={() => setPdfFile(null)}>Quitar</Button>
          </Box>
        )}

        {photos.length > 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
            {photos.map((photo, index) => (
              <Box key={photo.id} sx={{ position: "relative" }}>
                <Box
                  component="img"
                  src={photo.url}
                  alt={`Foto ${index + 1}`}
                  sx={{ width: "100%", height: 96, objectFit: "cover", borderRadius: "8px", bgcolor: "#F3F4F6" }}
                />
                <IconButton
                  size="small"
                  aria-label="Quitar foto"
                  disabled={saving}
                  onClick={() => removePhoto(photo.id)}
                  sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.55)", color: "#fff" }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {cameraOn && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            bgcolor: "#000",
            display: "flex",
            flexDirection: "column",
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{ flex: 1, width: "100%", objectFit: "cover", bgcolor: "#000" }}
          />
          {cameraError && (
            <Typography sx={{ color: "#fff", textAlign: "center", px: 2, py: 1 }}>{cameraError}</Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, p: 2 }}>
            <Button variant="outlined" onClick={stopCamera} sx={{ flex: 1, minHeight: 52, color: "#fff", borderColor: "#fff" }}>
              Listo ({photos.length})
            </Button>
            <Button variant="contained" onClick={takePhoto} disabled={photos.length >= MAX_PHOTOS} sx={{ flex: 1, minHeight: 52 }}>
              Tomar foto
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #EEF0F3" }}>
        <Button
          variant="contained"
          disableElevation
          fullWidth
          disabled={saving || pestLevelChosen === null}
          onClick={submit}
          sx={{ minHeight: 54, fontSize: 16, fontWeight: 700, borderRadius: "13px", textTransform: "none" }}
        >
          {saving ? (pdfFile ? "Subiendo..." : "Generando PDF y subiendo...") : "Guardar reporte"}
        </Button>
      </Box>
    </Box>
  );
}

const timeFieldSx = {
  "& .MuiInputBase-root": { minHeight: 52, fontSize: 18, borderRadius: "13px" },
};

const sectionLabelSx = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#374151",
  mb: 1,
};

const mediaBtnSx = {
  minHeight: 54,
  px: 0.5,
  borderRadius: "13px",
  textTransform: "none",
  fontWeight: 700,
};

const mediaBtnOutlinedSx = {
  ...mediaBtnSx,
  borderColor: "#E5E7EB",
  color: "#374151",
  "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
};
